import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { trackButtonClick } from '../../../services/tripPlanningService';

const HeroSection = ({ isAuthenticated, user, userProfile }) => {
  const [tripPrompt, setTripPrompt] = useState('');
  const navigate = useNavigate();

  const handleStartPlanning = async () => {
    // Track button click before navigation
    await trackButtonClick(user?.id);
    
    if (tripPrompt?.trim()) {
      // Navigate to AI trip planning interface with the prompt
      navigate('/ai-trip-planning-interface', { 
        state: { initialPrompt: tripPrompt } 
      });
    } else {
      navigate('/ai-trip-planning-interface');
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleStartPlanning();
    }
  };

  // Get display name from user profile or email
  const getDisplayName = () => {
    if (userProfile?.full_name) {
      return userProfile?.full_name?.split(' ')?.[0];
    }
    return user?.email?.split('@')?.[0] || 'there';
  };

  // Logged-in user view
  if (isAuthenticated) {
    return (
      <section className="relative bg-gradient-to-br from-primary/5 via-background to-accent/5 py-20 lg:py-32">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2560&q=80')] bg-cover bg-center opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="font-heading font-heading-bold text-4xl md:text-5xl lg:text-6xl text-foreground mb-6">
              Welcome back,
              <span className="text-primary block mt-2">{getDisplayName()}!</span>
            </h1>
            <p className="font-body text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Ready to plan your next adventure? Let's discover amazing state parks together!
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-card rounded-organic-lg shadow-organic-lg p-6 md:p-8 border border-border">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-14 h-14 flex items-center justify-center">
                  <img 
                    src="/assets/images/ChatGPT_Image_Nov_25_2025_09_47_06_AM-1764087412855.png" 
                    alt="Ranger YogiBot mascot - friendly cartoon bear with park ranger hat"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <h2 className="font-heading font-heading-bold text-xl text-card-foreground">Ranger YogiBot - AI Trip Planner</h2>
                  <p className="font-caption text-sm text-muted-foreground">Describe your ideal adventure experience</p>
                </div>
              </div>

              <div className="space-y-4">
                <Input
                  type="text"
                  placeholder="I want to plan a 3-day camping trip in a state park with hiking trails, lake activities, and family-friendly amenities..."
                  value={tripPrompt}
                  onChange={(e) => setTripPrompt(e?.target?.value)}
                  onKeyPress={handleKeyPress}
                  className="min-h-[120px] resize-none"
                  description="Tell us about your group size, preferred activities, accommodation type, and any special requirements"
                />
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    variant="default"
                    size="lg"
                    onClick={handleStartPlanning}
                    iconName="ArrowRight"
                    iconPosition="right"
                    className="flex-1 sm:flex-none"
                  >
                    Start Planning Your Trip
                  </Button>
                  
                  <Link to="/user-dashboard">
                    <Button
                      variant="outline"
                      size="lg"
                      iconName="MapPin"
                      iconPosition="left"
                      className="w-full sm:w-auto"
                    >
                      View My Trips
                    </Button>
                  </Link>
                  
                  <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                    <div className="flex items-center space-x-1">
                      <Icon name="Clock" size={16} />
                      <span>2-3 minutes</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Brain" size={24} color="var(--color-primary)" />
                </div>
                <h3 className="font-heading font-heading-bold text-lg text-foreground mb-2">AI-Powered Planning</h3>
                <p className="font-body text-muted-foreground">Smart recommendations based on your preferences and park features</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Download" size={24} color="var(--color-accent)" />
                </div>
                <h3 className="font-heading font-heading-bold text-lg text-foreground mb-2">Downloadable Itineraries</h3>
                <p className="font-body text-muted-foreground">Get detailed PDF guides with maps, schedules, and essential information</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Users" size={24} color="var(--color-success)" />
                </div>
                <h3 className="font-heading font-heading-bold text-lg text-foreground mb-2">Share & Collaborate</h3>
                <p className="font-body text-muted-foreground">Share trip plans with family and friends for group adventures</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Non-authenticated user view (original)
  return (
    <section className="relative bg-gradient-to-br from-primary/5 via-background to-accent/5 py-20 lg:py-32">
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2560&q=80')] bg-cover bg-center opacity-10"></div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="font-heading font-heading-bold text-4xl md:text-5xl lg:text-6xl text-foreground mb-6">
            Plan Your Perfect
            <span className="text-primary block mt-2">State Park Adventure</span>
          </h1>
          <p className="font-body text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Discover your next state park adventure, planned instantly by AI, personalized just for you!
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-card rounded-organic-lg shadow-organic-lg p-6 md:p-8 border border-border">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-14 h-14 flex items-center justify-center">
                <img 
                  src="/assets/images/ChatGPT_Image_Nov_25_2025_09_47_06_AM-1764087412855.png" 
                  alt="Ranger YogiBot mascot - friendly cartoon bear with park ranger hat"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h2 className="font-heading font-heading-bold text-xl text-card-foreground">Ranger YogiBot - AI Trip Planner</h2>
                <p className="font-caption text-sm text-muted-foreground">Plan now, describe your ideal adventure experience</p>
              </div>
            </div>

            <div className="space-y-4">
              <Input
                type="text"
                placeholder="I want to plan a 3-day camping trip in a state park with hiking trails, lake activities, and family-friendly amenities..."
                value={tripPrompt}
                onChange={(e) => setTripPrompt(e?.target?.value)}
                onKeyPress={handleKeyPress}
                className="min-h-[120px] resize-none"
                description="Tell us about your group size, preferred activities, accommodation type, and any special requirements"
              />
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="default"
                  size="lg"
                  onClick={handleStartPlanning}
                  iconName="ArrowRight"
                  iconPosition="right"
                  className="flex-1 sm:flex-none"
                >
                  Start Planning Your Trip
                </Button>
                
                <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <Icon name="Clock" size={16} />
                    <span>2-3 minutes</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Shield" size={16} />
                    <span>Free to use</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Brain" size={24} color="var(--color-primary)" />
              </div>
              <h3 className="font-heading font-heading-bold text-lg text-foreground mb-2">AI-Powered Planning</h3>
              <p className="font-body text-muted-foreground">Smart recommendations based on your preferences and park features</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Download" size={24} color="var(--color-accent)" />
              </div>
              <h3 className="font-heading font-heading-bold text-lg text-foreground mb-2">Downloadable Itineraries</h3>
              <p className="font-body text-muted-foreground">Get detailed PDF guides with maps, schedules, and essential information</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Users" size={24} color="var(--color-success)" />
              </div>
              <h3 className="font-heading font-heading-bold text-lg text-foreground mb-2">Share & Collaborate</h3>
              <p className="font-body text-muted-foreground">Share trip plans with family and friends for group adventures</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;